<template>
  <q-dialog :model-value="modelValue" maximized @update:model-value="$emit('update:modelValue', $event)">
    <q-card class="usp-report-card">
      <q-bar class="bg-primary text-white">
        <q-icon name="assessment" />
        <span class="q-ml-sm text-subtitle2">Отчёт УСП</span>
        <q-space />
        <q-btn icon="refresh" flat dense no-caps size="md" label="Обновить" :loading="data.refreshing" @click="$emit('refresh')" class="q-mr-sm" />
        <q-btn icon="close" flat dense no-caps size="md" label="Закрыть" v-close-popup />
      </q-bar>

      <div v-if="data.loading" class="q-pa-lg text-center">
        <q-spinner size="40px" color="primary" />
        <div class="q-mt-sm text-grey-6">Загрузка отчёта...</div>
      </div>

      <div v-else-if="!data.rows.length" class="q-pa-lg text-center text-grey-5">
        Нет данных
      </div>

      <template v-else>
        <div class="row q-pa-sm q-gutter-sm items-center" style="border-bottom:1px solid #e0e0e0; flex-wrap:wrap">
          <q-select
            v-model="data.filterType"
            :options="uspReportTypeOptions"
            label="Тип проекта"
            emit-value map-options clearable dense outlined
            style="min-width:200px"
            class="usp-filter-input"
          />
          <q-select
            v-model="data.filterBlock"
            :options="uspReportBlockOptions"
            label="Блок"
            emit-value map-options clearable dense outlined
            style="min-width:120px"
            class="usp-filter-input"
          />
          <q-select
            v-model="data.filterFloor"
            :options="uspReportFloorOptions"
            label="Этаж"
            emit-value map-options clearable dense outlined
            style="min-width:120px"
            class="usp-filter-input"
          />

          <q-input
            v-model="data.search"
            dense outlined clearable
            placeholder="Быстрый поиск…"
            style="width:220px"
            class="usp-filter-input"
          >
            <template #prepend><q-icon name="search" size="18px" /></template>
          </q-input>

          <q-input
            v-model="data.dateFrom"
            dense outlined clearable
            type="date"
            style="width:150px"
            class="usp-filter-input"
          >
            <template #prepend><q-icon name="event" size="16px" /></template>
          </q-input>
          <span class="text-caption text-grey-6">—</span>
          <q-input
            v-model="data.dateTo"
            dense outlined clearable
            type="date"
            style="width:150px"
            class="usp-filter-input"
          >
            <template #prepend><q-icon name="event" size="16px" /></template>
          </q-input>

          <q-btn v-if="data.search || data.dateFrom || data.dateTo || data.filterType || data.filterBlock || data.filterFloor != null" icon="filter_alt_off" flat dense no-caps size="md" label="Сбросить" color="grey-7" @click="uspReportResetFilters" />

          <q-space />

          <q-chip dense square icon="functions" color="grey-3" text-color="grey-8">
            Всего: {{ uspReportSummary.total }}<template v-if="uspReportSummary.total !== data.rows.length"> / {{ data.rows.length }}</template>
          </q-chip>
          <q-chip v-if="uspReportSummary.sent > 0" dense square icon="check_circle" color="green-2" text-color="green-9">
            Отправлено: {{ uspReportSummary.sent }}
          </q-chip>
          <q-chip v-if="uspReportSummary.notSent > 0" dense square icon="schedule" color="orange-2" text-color="orange-9">
            Не отправлено: {{ uspReportSummary.notSent }}
          </q-chip>
        </div>

        <q-scroll-area style="height: calc(100vh - 130px)">
          <q-markup-table dense flat separator="cell" class="usp-report-table">
            <thead>
              <tr class="bg-grey-2">
                <th class="text-center usp-th" style="width:100px" @click="uspReportToggleSort('is_sent')">
                  статус <q-icon v-if="data.sort.col==='is_sent'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-left usp-th" style="min-width:160px" @click="uspReportToggleSort('project_type_name')">
                  тип проекта <q-icon v-if="data.sort.col==='project_type_name'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-left usp-th" style="min-width:70px" @click="uspReportToggleSort('block_name')">
                  блок <q-icon v-if="data.sort.col==='block_name'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-left usp-th" style="min-width:50px" @click="uspReportToggleSort('floor_name')">
                  этаж <q-icon v-if="data.sort.col==='floor_name'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-left usp-th" style="min-width:140px" @click="uspReportToggleSort('constructive_name')">
                  конструктив <q-icon v-if="data.sort.col==='constructive_name'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-left usp-th" style="min-width:240px" @click="uspReportToggleSort('work_name')">
                  id/работа sbs <q-icon v-if="data.sort.col==='work_name'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-right usp-th" style="min-width:90px" @click="uspReportToggleSort('fact_value')">
                  объём факт <q-icon v-if="data.sort.col==='fact_value'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-right usp-th" style="min-width:100px" @click="uspReportToggleSort('fact_price')">
                  сумма смр <q-icon v-if="data.sort.col==='fact_price'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-right usp-th" style="min-width:110px" @click="uspReportToggleSort('materials_price_total')">
                  сумма материал <q-icon v-if="data.sort.col==='materials_price_total'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-right usp-th" style="min-width:110px; color:#0d47a1" @click="uspReportToggleSort('total_smr_mat')">
                  итого <q-icon v-if="data.sort.col==='total_smr_mat'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-center usp-th" style="min-width:90px" @click="uspReportToggleSort('last_value_date')">
                  дата факт <q-icon v-if="data.sort.col==='last_value_date'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-center usp-th" style="min-width:120px" @click="uspReportToggleSort('sent_at')">
                  дата отправки <q-icon v-if="data.sort.col==='sent_at'" :name="data.sort.dir==='asc'?'arrow_upward':'arrow_downward'" size="12px" />
                </th>
                <th class="text-left" style="min-width:240px">гуид усп</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(r, ri) in uspReportFilteredRows"
                :key="ri"
                class="cursor-pointer"
                :class="r.is_sent ? 'usp-row--sent' : 'usp-row--not-sent'"
                @click="$emit('open-work-detail', r)"
              >
                <td class="text-center">
                  <q-badge
                    :color="r.is_sent ? 'green' : 'yellow-8'"
                    :text-color="r.is_sent ? 'white' : 'black'"
                    :label="r.is_sent ? 'отправлено' : 'не отправлено'"
                    class="text-caption" style="font-size:10px"
                  />
                </td>
                <td class="text-left text-caption" style="max-width:200px; white-space:normal">{{ r.project_type_name || '—' }}</td>
                <td class="text-left text-caption">{{ r.block_name || '—' }}</td>
                <td class="text-left text-caption">{{ r.floor_name || '—' }}</td>
                <td class="text-left text-caption" style="max-width:200px; white-space:normal">{{ r.constructive_name || '—' }}</td>
                <td class="text-left text-caption" style="max-width:320px; white-space:normal">{{ r.work_sbs_id || '' }} / {{ r.work_name }}</td>
                <td class="text-right mono">{{ fmtNum(r.fact_value) }}</td>
                <td class="text-right mono">{{ fmtNum(r.fact_price) }}</td>
                <td class="text-right mono">{{ fmtNum(r.materials_price_total) }}</td>
                <td class="text-right mono" style="color:#0d47a1; font-weight:600">{{ fmtNum((r.fact_price || 0) + (r.materials_price_total || 0)) }}</td>
                <td class="text-center text-caption">{{ fmtDateRu(r.last_value_date) || '—' }}</td>
                <td class="text-center text-caption">{{ fmtDateTimeRu(r.sent_at) || '—' }}</td>
                <td class="text-left text-caption" style="max-width:260px">
                  <div v-if="r.usp_work_guid" style="display:flex;align-items:center;gap:2px">
                    <span
                      class="text-primary cursor-pointer"
                      style="text-decoration:underline; word-break:break-all"
                      @click.stop="$emit('open-guid-detail', r.usp_work_guid)"
                    >{{ r.usp_work_guid }}</span>
                    <q-btn flat dense round size="xs" icon="content_copy" color="grey-6"
                      @click.stop="copyToClipboard(r.usp_work_guid)">
                      <q-tooltip>Копировать GUID</q-tooltip>
                    </q-btn>
                  </div>
                  <span v-else class="text-grey-4">—</span>
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </q-scroll-area>

        <div class="usp-report-footer">
          <span class="text-caption text-grey-7" style="font-weight:600">ИТОГО</span>
          <span class="usp-footer-item"><b>Объём:</b> {{ fmtNum(uspReportSummary.sumFact) }}</span>
          <span class="usp-footer-item"><b>СМР:</b> {{ fmtNum(uspReportSummary.sumSmr) }}</span>
          <span class="usp-footer-item"><b>Материал:</b> {{ fmtNum(uspReportSummary.sumMat) }}</span>
          <span class="usp-footer-item" style="color:#0d47a1; font-weight:700"><b>Итого:</b> {{ fmtNum(uspReportSummary.sumSmr + uspReportSummary.sumMat) }}</span>
        </div>
      </template>
    </q-card>
  </q-dialog>
</template>

<script>
import { useFormatters } from '../composables/useFormatters';

export default {
  name: 'PpmUspReportDialog',
  props: {
    modelValue: Boolean,
    data: { type: Object, default: () => ({}) },
  },
  emits: ['update:modelValue', 'open-work-detail', 'open-guid-detail', 'refresh'],
  setup() {
    const { fmtNum, fmtKzt, fmtDateRu, fmtDateTimeRu } = useFormatters();
    return { fmtNum, fmtKzt, fmtDateRu, fmtDateTimeRu };
  },
  computed: {
    uspReportTypeOptions() {
      const set = new Set();
      for (const r of (this.data.rows || [])) {
        if (r.project_type_name) set.add(r.project_type_name);
      }
      return [...set].sort().map(v => ({ label: v, value: v }));
    },
    uspReportBlockOptions() {
      const set = new Set();
      for (const r of (this.data.rows || [])) {
        if (r.block_name) set.add(r.block_name);
      }
      return [...set].sort().map(v => ({ label: v, value: v }));
    },
    uspReportFloorOptions() {
      const set = new Set();
      for (const r of (this.data.rows || [])) {
        if (r.floor_name != null) set.add(r.floor_name);
      }
      return [...set].sort((a, b) => +a - +b).map(v => ({ label: `Этаж ${v}`, value: v }));
    },
    uspReportFilteredRows() {
      let rows = (this.data.rows || []).slice();

      if (this.data.filterType) {
        rows = rows.filter(r => r.project_type_name === this.data.filterType);
      }
      if (this.data.filterBlock) {
        rows = rows.filter(r => r.block_name === this.data.filterBlock);
      }
      if (this.data.filterFloor != null && this.data.filterFloor !== null) {
        rows = rows.filter(r => r.floor_name == this.data.filterFloor);
      }

      const q = (this.data.search || '').toLowerCase().trim();
      if (q) {
        rows = rows.filter(r => {
          const hay = [r.project_type_name, r.block_name, r.floor_name, r.constructive_name, r.work_sbs_id, r.work_name, r.usp_work_guid,
            r.is_sent ? 'отправлено' : 'не отправлено'].map(v => String(v ?? '').toLowerCase()).join(' ');
          return hay.includes(q);
        });
      }

      const df = this.data.dateFrom;
      const dt = this.data.dateTo;
      if (df) rows = rows.filter(r => (r.last_value_date || '') >= df);
      if (dt) rows = rows.filter(r => (r.last_value_date || '').slice(0, 10) <= dt);

      const s = this.data.sort;
      if (s && s.col) {
        const dir = s.dir === 'desc' ? -1 : 1;
        rows.sort((a, b) => {
          let va, vb;
          if (s.col === 'total_smr_mat') {
            va = (a.fact_price || 0) + (a.materials_price_total || 0);
            vb = (b.fact_price || 0) + (b.materials_price_total || 0);
          } else {
            va = a[s.col]; vb = b[s.col];
          }
          if (va == null) va = '';
          if (vb == null) vb = '';
          if (typeof va === 'number' || typeof vb === 'number') return ((+va || 0) - (+vb || 0)) * dir;
          return String(va).localeCompare(String(vb), 'ru') * dir;
        });
      }
      return rows;
    },
    uspReportSummary() {
      const rows = this.uspReportFilteredRows;
      let sent = 0, notSent = 0;
      let sumFact = 0, sumSmr = 0, sumMat = 0;
      for (const r of rows) {
        sumFact += r.fact_value             || 0;
        sumSmr  += r.fact_price             || 0;
        sumMat  += r.materials_price_total  || 0;
        if (r.is_sent) sent++; else notSent++;
      }
      return { total: rows.length, sent, notSent, sumFact, sumSmr, sumMat };
    },
  },
  methods: {
    uspReportToggleSort(col) {
      const s = this.data.sort;
      if (s.col === col) {
        s.dir = s.dir === 'asc' ? 'desc' : 'asc';
      } else {
        s.col = col;
        s.dir = 'asc';
      }
    },
    uspReportResetFilters() {
      this.data.search = '';
      this.data.dateFrom = '';
      this.data.dateTo = '';
      this.data.filterType = null;
      this.data.filterBlock = null;
      this.data.filterFloor = null;
      this.data.sort = { col: null, dir: 'asc' };
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$q.notify({ message: 'Скопировано', color: 'positive', timeout: 1000, position: 'top' });
      }).catch(() => {
        this.$q.notify({ message: 'Не удалось скопировать', color: 'negative', timeout: 1500, position: 'top' });
      });
    },
  },
};
</script>

<style scoped>
.usp-report-card {
  display: flex;
  flex-direction: column;
}
.usp-report-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  font-size: 11px;
  white-space: nowrap;
}
.usp-report-table th.usp-th {
  cursor: pointer;
  user-select: none;
  transition: background .15s;
}
.usp-report-table th.usp-th:hover {
  background: #e0e0e0;
}
.usp-filter-input {
  font-size: 12px;
}
.usp-filter-input :deep(.q-field__control) {
  min-height: 28px !important;
  height: 28px !important;
}
.usp-filter-input :deep(.q-field__native),
.usp-filter-input :deep(.q-field__append),
.usp-filter-input :deep(.q-field__prepend) {
  min-height: 28px !important;
  padding: 0 4px;
  font-size: 12px;
}
.usp-report-table td {
  font-size: 12px;
  padding: 4px 8px;
}
.usp-report-footer {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 8px 16px;
  background: #eceff1;
  border-top: 2px solid #b0bec5;
  font-family: 'Roboto Mono', monospace;
  font-size: 13px;
  flex-shrink: 0;
}
.usp-footer-item {
  white-space: nowrap;
}
.usp-report-table tbody tr:hover {
  background: #e3f2fd !important;
}
.usp-row--sent {
  background: #f0fdf4;
}
.usp-row--not-sent {
  background: #fefce8;
}
.mono {
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
}
</style>
